/**
 * Beheer > Checkout (checkout 11): één schakelregel met bevestiging in de
 * regel zelf, geen popup. De stand staat er altijd als woord. Na opslaan
 * staat de stand er die de server teruggeeft (de aanroeper zet die in de
 * cache); een fout blijft in de regel staan tot de volgende geslaagde
 * wijziging van die regel.
 *
 * Twee vormen: een knop (noodrem, onderdelen, signaal) of een vinkje
 * (ordercontrole live of droog). Beide vragen eerst bevestiging.
 */
import { useEffect, useRef, useState } from "react";

import { vertaalApiFout } from "@/lib/api";

export interface CheckoutRegelProps {
  /** Wordt `data-testid="schakelaar-<testid>"`. */
  testid: string;
  naam: string;
  aan: boolean;
  /** De noodrem: iets groter. */
  hoofd?: boolean;
  /** Het woord voor aan en uit. Standaard Aan en Uit. */
  woordAan?: string;
  woordUit?: string;
  /** Het werkwoord voor de omzetting. Standaard aanzetten en uitzetten. */
  doeAan?: string;
  doeUit?: string;
  /** Valt de stand op? Standaard: als hij uit staat. */
  opvallend?: boolean;
  vinkje?: boolean;
  /** Een andere schrijfactie op dezelfde rij loopt: dan even niets. */
  geblokt?: boolean;
  opZet: (aan: boolean) => Promise<void>;
}

export function CheckoutRegel({
  testid,
  naam,
  aan,
  hoofd = false,
  woordAan = "Aan",
  woordUit = "Uit",
  doeAan = "aanzetten",
  doeUit = "uitzetten",
  opvallend,
  vinkje = false,
  geblokt = false,
  opZet,
}: CheckoutRegelProps) {
  const [vraag, setVraag] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  // Toetsenbord en VoiceOver: na Ja of Annuleren terug naar de eigen knop.
  const knop = useRef<HTMLButtonElement | HTMLInputElement | null>(null);
  const terug = useRef(false);
  useEffect(() => {
    if (terug.current && !vraag && !bezig) {
      knop.current?.focus();
      terug.current = false;
    }
  }, [vraag, bezig]);
  const sluit = () => {
    terug.current = true;
    setVraag(false);
  };

  const wat = aan ? doeUit : doeAan;
  const valtOp = opvallend ?? !aan;
  const label = `${naam}, nu ${(aan ? woordAan : woordUit).toLowerCase()}: ${wat}`;
  const Doe = wat.charAt(0).toUpperCase() + wat.slice(1);

  const ja = async () => {
    setBezig(true);
    try {
      await opZet(!aan);
      setFout(null);
    } catch (e) {
      setFout(`${naam} ${wat} mislukt. ${vertaalApiFout(e, "")}`.trim());
    } finally {
      setBezig(false);
      sluit();
    }
  };

  return (
    <li className={`v6-bh-rij${hoofd ? " hoofd" : ""}`} data-testid={`schakelaar-${testid}`}>
      <span className="v6-bh-naam">{naam}</span>
      <span className={`v6-bh-stand${valtOp ? " uit" : ""}`}>{aan ? woordAan : woordUit}</span>
      {vinkje ? (
        <label className="c11-vinkje">
          <input
            type="checkbox"
            ref={(el) => {
              knop.current = el;
            }}
            checked={aan}
            aria-label={label}
            disabled={bezig || vraag || geblokt}
            onChange={() => setVraag(true)}
          />
          <span aria-hidden="true">{woordAan}</span>
        </label>
      ) : (
        <button
          type="button"
          className="v6-knop zacht"
          ref={(el) => {
            knop.current = el;
          }}
          aria-label={label}
          disabled={bezig || vraag || geblokt}
          onClick={() => setVraag(true)}
        >
          {Doe}
        </button>
      )}
      {vraag ? (
        <div className="v6-bh-vraag" role="group" aria-label={`${naam} ${wat}`}>
          <span>{`${naam} ${wat}?`}</span>
          {/* De noodrem-bevestiging is de ene oranje hoofdknop. */}
          <button
            type="button"
            className={`v6-knop ${hoofd ? "doe" : "acc"}`}
            autoFocus
            disabled={bezig || geblokt}
            onClick={() => void ja()}
          >
            {bezig ? "Bezig" : `Ja, ${wat}`}
          </button>
          <button type="button" className="v6-knop" disabled={bezig} onClick={sluit}>
            Annuleren
          </button>
        </div>
      ) : null}
      {fout ? (
        <div className="v6-fout" role="alert">
          {fout}
        </div>
      ) : null}
    </li>
  );
}
